'use client'

import { ArrowRight, CircleAlert, Unplug } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { cn } from '@/src/utils/styles'
import { Button } from './button'
import { Close } from './icons/close'
import { PauseCircle } from './icons/pause-circle'
import { SuccessCircle } from './icons/success-circle'
import { Spinner } from './spinner'

const ICONS = {
  success: (
    <div className='size-12 rounded-full flex items-center justify-center bg-success'>
      <SuccessCircle className='size-6' color='#57DFC2' />
    </div>
  ),
  error: (
    <div className='size-12 rounded-full flex items-center justify-center bg-danger'>
      <svg
        width='25'
        height='25'
        viewBox='0 0 25 25'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M12.5 24.5C19.1274 24.5 24.5 19.1274 24.5 12.5C24.5 5.87258 19.1274 0.5 12.5 0.5C5.87258 0.5 0.5 5.87258 0.5 12.5C0.5 19.1274 5.87258 24.5 12.5 24.5ZM17.5298 7.46958C17.8227 7.76242 17.8228 8.23729 17.53 8.53024L13.5597 12.5019L17.53 16.4735C17.8228 16.7665 17.8227 17.2413 17.5298 17.5342C17.2368 17.827 16.762 17.8269 16.4691 17.534L12.4993 13.5627L8.52942 17.534C8.23658 17.8269 7.76171 17.827 7.46876 17.5342C7.17582 17.2413 7.17573 16.7665 7.46858 16.4735L11.4388 12.5019L7.46858 8.53024C7.17573 8.23729 7.17582 7.76242 7.46876 7.46958C7.76171 7.17673 8.23658 7.17682 8.52942 7.46976L12.4993 11.441L16.4691 7.46976C16.762 7.17682 17.2368 7.17673 17.5298 7.46958Z'
          fill='#FC6779'
        />
      </svg>
    </div>
  ),
  paused: (
    <div className='size-12 rounded-full flex items-center justify-center bg-surface2'>
      <PauseCircle className='size-6' />
    </div>
  ),
  offline: (
    <div className='size-12 rounded-full flex items-center justify-center bg-surface2'>
      <Spinner className='size-6 text-accent-primary' />
    </div>
  ),
}

interface ToastBase {
  t: string | number
  tradeId?: string
  variant: 'success' | 'error' | 'paused' | 'offline'
  message?: string | { from: string; to: string }
  description?: string
  descriptionClassName?: string
  // Route paths where the toasts shouldn't be displayed
  hiddenPaths?: string[]
  // Optional custom icon to override the default variant icon
  customIcon?: React.ReactNode
}

const ToastBase = ({
  tradeId,
  variant,
  message,
  description,
  descriptionClassName,
  t,
  hiddenPaths,
  customIcon,
}: ToastBase) => {
  const router = useRouter()
  const pathname = usePathname()

  const action = useCallback(() => {
    if (tradeId) {
      router.push(`/route/${tradeId}`)
      toast.dismiss(t)
    }
  }, [router, t, tradeId])

  const hidden = useMemo(
    () => pathname.split('/').some((p) => hiddenPaths?.includes(p)),
    [hiddenPaths, pathname],
  )

  return (
    <div className='relative w-full h-full'>
      <Link
        href={tradeId ? `/route/${tradeId}` : ''}
        onClick={() => {
          toast.dismiss(t)
        }}
        className='h-full'
      >
        <Button
          type='button'
          onClick={action}
          className='w-full h-full before:rounded-lg before:p-0 before:inset-0 [font-size:unset]'
          data-hide={hidden}
          size='sm'
        >
          <div className='flex items-center gap-4 w-full h-full px-4'>
            {customIcon ?? ICONS[variant]}
            <div className='flex flex-col gap-1 text-left'>
              <p className='text-primary font-semibold inline-flex items-center gap-1 leading-6'>
                {message && typeof message !== 'string' ? (
                  <>
                    <span>{message.from}</span>
                    <ArrowRight width={16} height={16} strokeWidth={3} />
                    <span>{message.to}</span>
                  </>
                ) : (
                  <span>{message}</span>
                )}
              </p>
              <span
                className={cn(
                  'text-secondary text-sm font-normal',
                  descriptionClassName,
                )}
              >
                {description}
              </span>
            </div>
          </div>
        </Button>
      </Link>
      <button
        type='button'
        className='absolute z-10 right-4 top-6 size-8 flex items-center justify-center opacity-30 transition-opacity hover:opacity-100'
        onClick={(e) => {
          e.stopPropagation()
          toast.dismiss(t)
        }}
      >
        <Close width={16} height={16} className='text-button-secondary-text' />
      </button>
    </div>
  )
}

interface NewWalletDetectedToastProps {
  walletImg: string
  walletName: string
  accountName: string
  onAddAccount: () => void
  onDismiss?: () => void
}

const NewWalletAccountDetectedToast = ({
  walletImg,
  walletName,
  accountName,
  toastId,
  onAddAccount,
  onDismiss,
}: NewWalletDetectedToastProps & { toastId: string | number }) => (
  <div className='relative w-full h-full'>
    <Button
      type='button'
      onClick={onAddAccount}
      className='w-full h-full before:rounded-lg before:p-0 before:inset-0 [font-size:unset]'
      size='sm'
    >
      <div className='flex items-center gap-4 w-full h-full px-4'>
        <Image
          className='self-start size-10 overflow-hidden rounded-md my-auto'
          src={walletImg}
          alt={`${walletName} logo`}
          width={40}
          height={40}
          unoptimized
        />
        <div className='flex flex-col gap-1 text-left'>
          <p className='text-primary font-semibold inline-flex items-center gap-1 leading-6'>
            <span>New wallet detected</span>
          </p>
          <span className='text-secondary text-sm font-normal'>
            {accountName}
          </span>
        </div>
      </div>
    </Button>
    <button
      type='button'
      className='absolute z-10 right-4 top-6 size-8 flex items-center justify-center opacity-30 transition-opacity hover:opacity-100'
      onClick={(e) => {
        e.stopPropagation()
        onDismiss?.()
        toast.dismiss(toastId)
      }}
    >
      <Close width={16} height={16} className='text-button-secondary-text' />
    </button>
  </div>
)

type NewWalletAddedToastProps = Omit<
  NewWalletDetectedToastProps,
  'onAddAccount'
>

const NewWalletAccountAddedToast = ({
  walletImg,
  walletName,
  accountName,
  toastId,
  onDismiss,
}: NewWalletAddedToastProps & { toastId: string | number }) => (
  <div className='relative w-full h-full'>
    <Button
      type='button'
      className='w-full h-full before:rounded-lg before:p-0 before:inset-0 [font-size:unset]'
      size='sm'
    >
      <div className='flex items-center gap-4 w-full h-full px-4'>
        <Image
          className='self-start size-10 overflow-hidden rounded-md my-auto'
          src={walletImg}
          alt={`${walletName} logo`}
          width={40}
          height={40}
          unoptimized
        />
        <div className='flex flex-col gap-1 text-left'>
          <p className='text-primary font-semibold inline-flex items-center gap-1 leading-6'>
            <span>Wallet added to portfolio</span>
          </p>
          <span className='text-secondary text-sm font-normal'>
            {accountName}
          </span>
        </div>
      </div>
    </Button>
    <button
      type='button'
      className='absolute z-10 right-4 top-6 size-8 flex items-center justify-center opacity-30 transition-opacity hover:opacity-100'
      onClick={(e) => {
        e.stopPropagation()
        onDismiss?.()
        toast.dismiss(toastId)
      }}
    >
      <Close width={16} height={16} className='text-button-secondary-text' />
    </button>
  </div>
)

type NewWalletConnectedToastProps = Omit<
  NewWalletDetectedToastProps,
  'onAddAccount'
>

const NewWalletConnectedToast = ({
  walletImg,
  walletName,
  accountName,
  toastId,
  onDismiss,
}: NewWalletConnectedToastProps & { toastId: string | number }) => (
  <div className='relative w-full h-full'>
    <Button
      type='button'
      className='w-full h-full before:rounded-lg before:p-0 before:inset-0 [font-size:unset]'
      size='sm'
    >
      <div className='flex items-center gap-4 w-full h-full px-4'>
        <Image
          className='self-start size-10 overflow-hidden rounded-md my-auto'
          src={walletImg}
          alt={`${walletName} logo`}
          width={40}
          height={40}
          unoptimized
        />
        <div className='flex flex-col gap-1 text-left'>
          <p className='text-primary font-semibold inline-flex items-center gap-1 leading-6'>
            <span>{walletName} connected</span>
          </p>
          <span className='text-secondary text-sm font-normal'>
            For {accountName}
          </span>
        </div>
      </div>
    </Button>
    <button
      type='button'
      className='absolute z-10 right-4 top-6 size-8 flex items-center justify-center opacity-30 transition-opacity hover:opacity-100'
      onClick={(e) => {
        e.stopPropagation()
        onDismiss?.()
        toast.dismiss(toastId)
      }}
    >
      <Close width={16} height={16} className='text-button-secondary-text' />
    </button>
  </div>
)

interface WalletsDisconnectedToastProps {
  toastId: string | number
  numberOfWallets: number
}

const WalletsDisconnectedToast = ({
  toastId,
  numberOfWallets,
}: WalletsDisconnectedToastProps) => (
  <div className='relative w-full h-full'>
    <div className='flex items-center gap-4 w-full h-full px-4'>
      <div className='size-10 rounded-lg flex items-center justify-center bg-surface1'>
        <Unplug className='size-5 text-tertiary' />
      </div>
      <div className='flex flex-col gap-1 text-left'>
        <p className='text-primary font-semibold inline-flex items-center gap-1 leading-6'>
          <span>Wallets disconnected</span>
        </p>
        <span className='text-secondary text-sm font-normal'>
          {numberOfWallets} wallet{numberOfWallets > 1 ? 's' : ''}
        </span>
      </div>
    </div>
    <button
      type='button'
      className='absolute z-10 right-4 top-6 size-8 flex items-center justify-center opacity-30 transition-opacity hover:opacity-100'
      onClick={(e) => {
        e.stopPropagation()
        toast.dismiss(toastId)
      }}
    >
      <Close width={16} height={16} className='text-button-secondary-text' />
    </button>
  </div>
)

export const showWalletsDisconnectedToast = ({
  numberOfWallets,
}: {
  numberOfWallets: number
}) => {
  const toastId = 'wallets-disconnected'

  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  return toast.custom(
    (t) => (
      <WalletsDisconnectedToast toastId={t} numberOfWallets={numberOfWallets} />
    ),
    {
      id: toastId,
    },
  )
}

export const showNewWalletDetectedToast = ({
  toastId,
  walletImg,
  walletName,
  accountName,
  onAddAccount,
  onDismiss,
}: NewWalletDetectedToastProps & { toastId: string }) => {
  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  return toast.custom(
    (t) => (
      <NewWalletAccountDetectedToast
        walletImg={walletImg}
        walletName={walletName}
        accountName={accountName}
        toastId={t}
        onAddAccount={onAddAccount}
        onDismiss={onDismiss}
      />
    ),
    {
      id: toastId,
      duration: Infinity,
    },
  )
}

export const showNewWalletAccountAddedToast = ({
  toastId,
  walletImg,
  walletName,
  accountName,
  onDismiss,
}: NewWalletAddedToastProps & { toastId: string }) => {
  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  // Don't show if Keplr invalid state toast is active
  if (toasts.some((t) => t.id === 'keplr-invalid-state')) {
    return
  }

  return toast.custom(
    (t) => (
      <NewWalletAccountAddedToast
        walletImg={walletImg}
        walletName={walletName}
        accountName={accountName}
        toastId={t}
        onDismiss={onDismiss}
      />
    ),
    {
      id: toastId,
      duration: 5000,
    },
  )
}

export const showNewWalletConnectedToast = ({
  toastId,
  walletImg,
  walletName,
  accountName,
  onDismiss,
}: NewWalletConnectedToastProps & { toastId: string }) => {
  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  return toast.custom(
    (t) => (
      <NewWalletConnectedToast
        walletImg={walletImg}
        walletName={walletName}
        accountName={accountName}
        toastId={t}
        onDismiss={onDismiss}
      />
    ),
    {
      id: toastId,
      duration: Infinity,
    },
  )
}

export const successToast = (
  message: string | { from: string; to: string },
  description?: string,
  hiddenPaths?: string[],
) => {
  const toastId = `success-${typeof message === 'string' ? message : `${message.from}-${message.to}`}-${description}`

  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  return toast.custom(
    (t) => (
      <ToastBase
        t={t}
        variant='success'
        message={message}
        description={description}
        hiddenPaths={hiddenPaths}
      />
    ),
    {
      id: toastId,
      duration: 5000,
    },
  )
}

export const tradeSuccessToast = (
  message: string | { from: string; to: string },
  tradeId: string,
  value?: string,
  destinationChain?: string,
  hiddenPaths?: string[],
) => {
  const toastId = `success-${tradeId}`

  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((toast) => toast.id === toastId)) {
    return
  }

  toast.custom(
    (t) => (
      <ToastBase
        t={t}
        variant='success'
        message={message}
        description={`${value} received on ${destinationChain}`}
        tradeId={tradeId}
        hiddenPaths={hiddenPaths}
      />
    ),
    {
      id: toastId,
      duration: 5000,
    },
  )
}

export const tradeFailureToast = (
  message: string | { from: string; to: string },
  tradeId: string,
  reason: string,
  hiddenPaths?: string[],
) => {
  const toastId = `failure-${tradeId}`
  const toasts = toast.getToasts()

  // Prevent duplicate toasts
  if (toasts.some((toast) => toast.id === toastId)) {
    return
  }

  toast.custom(
    (t) => (
      <ToastBase
        t={t}
        variant='error'
        message={message}
        description={reason}
        descriptionClassName='text-danger-text max-w-[250px] truncate'
        tradeId={tradeId}
        hiddenPaths={hiddenPaths}
      />
    ),
    {
      duration: Number.POSITIVE_INFINITY,
    },
  )
}

export const tradeRecoveringToast = (
  message: string | { from: string; to: string },
  tradeId: string,
  recoveryTicker: string,
  destinationAssetLogoUrl: string,
  destinationAssetTicker: string,
  hiddenPaths?: string[],
) => {
  const toastId = `recovering-${tradeId}`
  const toasts = toast.getToasts()

  // Prevent duplicate toasts
  if (toasts.some((toast) => toast.id === toastId)) {
    return
  }

  const customIcon = (
    <div className='relative rounded-full size-10 flex items-center justify-center'>
      <Image
        src={destinationAssetLogoUrl}
        alt={destinationAssetTicker}
        width={40}
        height={40}
        className='rounded-full'
      />
      <div className='absolute z-10 -bottom-1.5 -right-1.5 size-7 bg-background flex items-center justify-center rounded-full'>
        <CircleAlert className='text-danger-text size-5' />
      </div>
    </div>
  )

  toast.custom(
    (t) => (
      <ToastBase
        t={t}
        variant='error'
        message={message}
        description={`Recovering funds as ${recoveryTicker}`}
        descriptionClassName='max-w-[250px] truncate'
        tradeId={tradeId}
        hiddenPaths={hiddenPaths}
        customIcon={customIcon}
      />
    ),
    {
      duration: Number.POSITIVE_INFINITY,
      id: toastId,
    },
  )
}

export const tradePausedToast = (
  message: string | { from: string; to: string },
  reason: string,
  tradeId?: string,
  hiddenPaths?: string[],
) => {
  const toastId = `paused-${tradeId}`

  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  return toast.custom(
    (t) => (
      <ToastBase
        t={t}
        variant='paused'
        message={message}
        description={reason}
        descriptionClassName='text-link'
        tradeId={tradeId}
        hiddenPaths={hiddenPaths}
      />
    ),
    {
      duration: Number.POSITIVE_INFINITY,
      id: toastId,
    },
  )
}

export const offlineToast = (hiddenPaths?: string[]) => {
  const toastId = 'offline-toast'

  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  return toast.custom(
    (t) => (
      <ToastBase
        t={t}
        variant='offline'
        message="You're offline"
        description='Trying to reconnect...'
        hiddenPaths={hiddenPaths}
      />
    ),
    {
      duration: Number.POSITIVE_INFINITY,
      id: toastId,
    },
  )
}

export const walletStatusToast = (
  type: 'connected' | 'disconnected',
  walletName: string,
  hiddenPaths?: string[],
) => {
  const toastId = `wallet-status-${walletName}-${type}`

  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  return toast.custom(
    (t) => (
      <ToastBase
        t={t}
        variant='success'
        message={`${walletName} ${type}`}
        hiddenPaths={hiddenPaths}
      />
    ),
    {
      id: toastId,
      duration: 5000,
    },
  )
}

export const errorToast = (error: Error | string, hiddenPaths?: string[]) => {
  const errorMessage = typeof error === 'string' ? error : error.message
  const toastId = `error-${errorMessage}`

  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  return toast.custom(
    (t) => (
      <ToastBase
        t={t}
        variant='error'
        message={errorMessage}
        hiddenPaths={hiddenPaths}
      />
    ),
    {
      id: toastId,
      duration: 8_000,
    },
  )
}

export const keplrInvalidStateToast = (connectedWallet?: {
  disconnect: () => void
}) => {
  const toastId = 'keplr-invalid-state'

  // Prevent duplicate toasts
  const toasts = toast.getToasts()
  if (toasts.some((t) => t.id === toastId)) {
    return
  }

  return toast.custom(
    (t) => (
      <div className='relative w-full h-full'>
        <div className='flex items-center gap-4 w-full h-full px-4'>
          {ICONS.error}
          <div className='flex grow flex-col gap-1 text-left'>
            <p className='text-primary font-semibold inline-flex items-center gap-1 leading-6'>
              <span>Keplr is in an invalid state, please refresh</span>
            </p>
          </div>
          <Button
            type='button'
            size='sm'
            onClick={(e) => {
              e.stopPropagation()
              toast.dismiss(t)
              // Disconnect the wallet if available
              connectedWallet?.disconnect()
              if (typeof window !== 'undefined') {
                window.location.reload()
              }
            }}
          >
            Refresh
          </Button>
        </div>
      </div>
    ),
    { id: toastId, duration: Number.POSITIVE_INFINITY },
  )
}
